import React, { useState } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  SelectItem,
  Chip,
  type Selection
} from '@heroui/react';
import { inviteUserToEmpresa } from '@/services/empresa/users.service';
import { useAuthStore } from '@/stores/auth.store';

interface InvitationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (success: boolean) => void;
}

interface RoleOption {
  id: string;
  name: string;
}

const InvitationModal: React.FC<InvitationModalProps> = ({ 
  isOpen, 
  onClose, 
  onSubmit 
}) => {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    roleId: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { user } = useAuthStore();

  // Define role options
  const roleOptions: RoleOption[] = [
    { id: 'Administrador', name: 'Administrador' },
    { id: 'Colaborador', name: 'Colaborador' }
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleRoleChange = (keys: Selection) => {
    const roleId = Array.from(keys as Set<string>)[0];
    setFormData(prev => ({
      ...prev,
      roleId: roleId || ''
    }));
    
    // Clear error when user selects a role
    if (errors.roleId) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.roleId;
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.nombre || formData.nombre.trim().length < 2) {
      newErrors.nombre = 'El nombre debe tener al menos 2 caracteres';
    }
    
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Por favor ingrese un correo electrónico válido';
    }
    
    if (!formData.roleId) {
      newErrors.roleId = 'Por favor seleccione un rol';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      if (!user?.empresa) {
        throw new Error('No se pudo obtener la empresa del usuario autenticado.');
      }
      // Make API call to invite user to empresa
      await inviteUserToEmpresa(
        formData.email,
        formData.nombre,
        formData.roleId,
        user?.empresa
      );
      
      // Reset form and close modal after successful invitation
      setFormData({
        nombre: '',
        email: '',
        roleId: ''
      });
      
      onSubmit(true);
      onClose();
    } catch (error: any) {
      console.error('Error inviting user:', error);
      setErrors({ submit: 'Error al enviar la invitación. Por favor intente nuevamente.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset form and errors when closing
    setFormData({
      nombre: '',
      email: '',
      roleId: ''
    });
    setErrors({});
    onClose();
  };

  return (
    <Modal 
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      placement="top-center"
      backdrop="blur"
    >
      <ModalContent>
        {(_onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              Nueva Invitación
            </ModalHeader>
            <ModalBody>
              <form id="invitation-form" onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <Input
                    label="Nombre"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    variant="bordered"
                    isInvalid={!!errors.nombre}
                    errorMessage={errors.nombre}
                    isRequired
                    placeholder="Ingrese el nombre"
                  />
                  
                  <Input
                    label="Correo Electrónico"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    variant="bordered"
                    isInvalid={!!errors.email}
                    errorMessage={errors.email}
                    isRequired
                    placeholder="ejemplo@empresa.com"
                  />
                  
                  <Select
                    label="Rol"
                    selectedKeys={formData.roleId ? [formData.roleId] : []}
                    onSelectionChange={handleRoleChange}
                    variant="bordered"
                    isInvalid={!!errors.roleId}
                    errorMessage={errors.roleId}
                    isRequired
                    placeholder="Seleccione un rol"
                  >
                    {roleOptions.map((role) => (
                      <SelectItem key={role.id}>
                        {role.name}
                      </SelectItem>
                    ))}
                  </Select>
                  
                  <div className="pt-2">
                    <p className="text-sm text-gray-500">Información de la invitación</p>
                    <div className="mt-1 space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm">Nombre:</span>
                        <span className="text-sm font-medium">{formData.nombre || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm">Email:</span>
                        <span className="text-sm font-medium">{formData.email || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm">Rol:</span>
                        <Chip 
                          color="primary" 
                          variant="flat"
                          size="sm"
                        >
                          {roleOptions.find(r => r.id === formData.roleId)?.name || '-'}
                        </Chip>
                      </div>
                    </div>
                  </div>
                </div>
                
                {errors.submit && (
                  <div className="mt-4 p-3 bg-danger/20 text-danger text-sm rounded-md">
                    {errors.submit}
                  </div>
                )}
              </form>
            </ModalBody>
            <ModalFooter>
              <Button color="danger" variant="flat" onClick={handleClose}>
                Cancelar
              </Button>
              <Button 
                color="primary" 
                type="submit" 
                form="invitation-form"
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
              >
                Enviar Invitación
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default InvitationModal;